import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MOSSMATRIX_CONTACT_EMAIL } from "../lib/chatbotKnowledge";
import { getSubmitContactUrl } from "../lib/checkoutConfig";
import { useShellTheme } from "./layout/ShellTheme";
import { HOME_CTA_DARK_PRIMARY } from "../lib/homeCta";

const TURNSTILE_SCRIPT = "https://challenges.cloudflare.com/turnstile/v0/api.js";
const SITE_KEY = (process.env.REACT_APP_TURNSTILE_SITE_KEY || "").trim();

/**
 * Contact form with honeypot + optional Cloudflare Turnstile.
 * Server verification: Netlify `submit-contact` + env `TURNSTILE_SECRET_KEY` (not in frontend).
 * Future: rate-limit in that function (e.g. IP + sliding window in KV or Supabase).
 */
export default function ContactForm() {
  const dark = useShellTheme() === "dark";
  const location = useLocation();
  const suggestBotanicalSeededRef = useRef(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [orderRef, setOrderRef] = useState("");
  const [honeypot, setHoneypot] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const widgetIdRef = useRef(null);
  const turnstileMountRef = useRef(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    if (params.get("topic") !== "suggest-botanical" || suggestBotanicalSeededRef.current) return;
    suggestBotanicalSeededRef.current = true;
    setMessage((prev) =>
      prev.trim()
        ? prev
        : "I would like to suggest a powdered botanical for a future Moss Matrix launch blend menu:\n\n",
    );
  }, [location.search]);

  const resetTurnstile = useCallback(() => {
    if (typeof window === "undefined" || !window.turnstile || widgetIdRef.current == null) return;
    try {
      window.turnstile.reset(widgetIdRef.current);
    } catch {
      /* ignore */
    }
    setTurnstileToken("");
  }, []);

  useEffect(() => {
    if (!SITE_KEY || !turnstileMountRef.current) return;

    const mount = () => {
      if (!turnstileMountRef.current || !window.turnstile) return;
      if (widgetIdRef.current != null) return;
      widgetIdRef.current = window.turnstile.render(turnstileMountRef.current, {
        sitekey: SITE_KEY,
        callback: (token) => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
    };

    if (window.turnstile) {
      mount();
      return () => {
        if (widgetIdRef.current != null && window.turnstile) {
          try {
            window.turnstile.remove(widgetIdRef.current);
          } catch {
            /* ignore */
          }
        }
        widgetIdRef.current = null;
      };
    }

    let s = document.querySelector(`script[src="${TURNSTILE_SCRIPT}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = TURNSTILE_SCRIPT;
      s.async = true;
      s.defer = true;
      document.body.appendChild(s);
    }
    s.addEventListener("load", mount);
    return () => {
      s?.removeEventListener("load", mount);
      if (widgetIdRef.current != null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
        } catch {
          /* ignore */
        }
      }
      widgetIdRef.current = null;
    };
  }, []);

  const mailtoFallback = () => {
    const subject = encodeURIComponent(`MossMatrix contact from ${name || "website"}`);
    const body = encodeURIComponent(
      `Name: ${name}\nEmail: ${email}\nOrder ref: ${orderRef || "-"}\n\n${message}\n`,
    );
    window.location.href = `mailto:${MOSSMATRIX_CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (honeypot.trim()) {
      toast.success("Thanks. We will be in touch.");
      return;
    }
    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please add your name, email, and a short message.");
      return;
    }
    if (SITE_KEY && !turnstileToken) {
      toast.error("Please complete the security check, or try again in a moment.");
      return;
    }

    setSubmitting(true);
    const payload = {
      name: name.trim(),
      email: email.trim(),
      message: message.trim(),
      orderRef: orderRef.trim() || undefined,
      turnstileToken: turnstileToken || undefined,
      website: honeypot,
    };

    try {
      const url = getSubmitContactUrl();
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      let data = null;
      try {
        data = await res.json();
      } catch {
        data = null;
      }
      if (res.ok && data && data.ok) {
        toast.success("Message received. We read every email. You will hear from a human soon.");
        setMessage("");
        setOrderRef("");
        resetTurnstile();
        setSubmitting(false);
        return;
      }
    } catch {
      /* fall through to mailto */
    }

    setSubmitting(false);
    toast.message("Opening your email app…", { description: "Send the message to us directly, or try again later." });
    mailtoFallback();
    resetTurnstile();
  };

  return (
    <form onSubmit={handleSubmit} className="relative max-w-xl rounded-3xl border border-cream bg-white p-8 md:p-10 mb-10 space-y-5">
      <p className="text-sm text-botanical/85 leading-relaxed">
        Prefer email?{" "}
        <a href={`mailto:${MOSSMATRIX_CONTACT_EMAIL}`} className="text-forest font-medium underline hover:text-gold">
          {MOSSMATRIX_CONTACT_EMAIL}
        </a>
        . You can also use the MossMatrix helper chat on any page for quick product questions, then email us here for anything detailed.
      </p>

      <div>
        <label htmlFor="contact-name" className="text-xs uppercase tracking-widest text-botanical/70">
          Name
        </label>
        <input
          id="contact-name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 w-full rounded-2xl border border-cream bg-offwhite px-4 py-3 text-sm text-forest"
          autoComplete="name"
          required
        />
      </div>
      <div>
        <label htmlFor="contact-email" className="text-xs uppercase tracking-widest text-botanical/70">
          Email
        </label>
        <input
          id="contact-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="mt-1 w-full rounded-2xl border border-cream bg-offwhite px-4 py-3 text-sm text-forest"
          autoComplete="email"
          required
        />
      </div>
      <div>
        <label htmlFor="contact-order" className="text-xs uppercase tracking-widest text-botanical/70">
          Order number <span className="text-botanical/50 normal-case">(optional)</span>
        </label>
        <input
          id="contact-order"
          value={orderRef}
          onChange={(e) => setOrderRef(e.target.value)}
          className="mt-1 w-full rounded-2xl border border-cream bg-offwhite px-4 py-3 text-sm text-forest"
          autoComplete="off"
        />
      </div>
      <div>
        <label htmlFor="contact-message" className="text-xs uppercase tracking-widest text-botanical/70">
          Message
        </label>
        <textarea
          id="contact-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={5}
          required
          className="mt-1 w-full rounded-2xl border border-cream bg-offwhite px-4 py-3 text-sm text-forest resize-y min-h-[120px]"
        />
      </div>

      {/* Honeypot: leave hidden; bots often fill this. */}
      <div className="absolute -left-[9999px] w-px h-px overflow-hidden" aria-hidden="true">
        <label htmlFor="contact-website">Website</label>
        <input
          id="contact-website"
          tabIndex={-1}
          autoComplete="off"
          value={honeypot}
          onChange={(e) => setHoneypot(e.target.value)}
        />
      </div>

      {SITE_KEY ? (
        <div>
          <p className="text-[11px] text-botanical/65 mb-2">Security check (Cloudflare Turnstile)</p>
          <div ref={turnstileMountRef} className="min-h-[65px]" />
        </div>
      ) : (
        <p className="text-[11px] text-botanical/60 leading-relaxed">
          Turnstile is not configured in this environment. Set <code className="text-[10px] bg-cream px-1 rounded">REACT_APP_TURNSTILE_SITE_KEY</code>{" "}
          for production. Submissions still use a honeypot; connect{" "}
          <code className="text-[10px] bg-cream px-1 rounded">submit-contact</code> on Netlify with{" "}
          <code className="text-[10px] bg-cream px-1 rounded">TURNSTILE_SECRET_KEY</code> when ready.
        </p>
      )}

      <button
        type="submit"
        disabled={submitting}
        className={
          dark
            ? `${HOME_CTA_DARK_PRIMARY} w-full py-3.5 text-sm disabled:opacity-60`
            : "w-full rounded-full bg-forest text-cream py-3.5 text-sm font-medium hover:bg-botanical transition disabled:opacity-60"
        }
      >
        {submitting ? "Sending…" : "Send message"}
      </button>

      <p className="text-[10px] text-botanical/55 leading-relaxed">
        Guest checkout only, no account needed. Email receipts after payment may arrive separately.{" "}
        {/* Future Netlify function should apply rate limits (e.g. per IP) to reduce spam. */}
      </p>
    </form>
  );
}
